import { useMemo } from 'react';
import { Box, Button, LinearProgress, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useGetDocumentReadinessSummaryQuery } from '@/apis/extended/documentRequirementsApi';
import { useGetMyLoanApplicationsQuery } from '@/apis/extended/loanApplicationApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { ChecklistSection } from '@/components/CommercialLoanApplication/ChecklistSection';
import {
  mergeLoanChecklistWithProfileDocuments,
  mortgageChecklistProgress,
} from '@/utils/document';
import { pickPurchaseMasterApplication } from '@/utils/loanApplicationPick';

type Props = {
  /** Dialog / compact embed — less vertical padding */
  variant?: 'page' | 'embedded';
  showOpenApplicationButton?: boolean;
};

export function MortgageDocumentsWorkspace({
  variant = 'page',
  showOpenApplicationButton = true,
}: Props) {
  const navigate = useNavigate();
  const { data: user } = useGetCurrentUserQuery();
  const { data: docReadiness } = useGetDocumentReadinessSummaryQuery(
    undefined,
    { skip: !user }
  );
  const { data: apps = [], isLoading } = useGetMyLoanApplicationsQuery();

  const file = useMemo(
    () => pickPurchaseMasterApplication(apps) ?? null,
    [apps]
  );

  const checklistRows = useMemo(() => {
    if (!file || !user) return [];
    return mergeLoanChecklistWithProfileDocuments(user, file, docReadiness);
  }, [file, user, docReadiness]);

  const progress = useMemo(
    () => mortgageChecklistProgress(checklistRows),
    [checklistRows]
  );

  const checklistReadOnly =
    file != null && file.status !== 'draft' && file.status !== 'declined';

  const Shell = variant === 'page' ? Paper : Box;
  const shellSx = variant === 'page' ? { p: 2, mb: 2 } : { pt: 1 };

  if (isLoading) {
    return (
      <Shell sx={shellSx}>
        <Typography variant="body2" color="text.secondary">
          Loading documents…
        </Typography>
      </Shell>
    );
  }

  if (!file) {
    return (
      <Shell sx={shellSx}>
        <Typography variant="body2" color="text.secondary">
          Start a mortgage application from the hub to track required documents
          here. Your loan type and program on the Loan type tab determine which
          documents are requested.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 2 }}
          onClick={() => navigate('/loan')}
        >
          Go to My applications
        </Button>
      </Shell>
    );
  }

  if (!user?.loanEstimationType || !user?.loanProgram || !user?.occupancy) {
    return (
      <Shell sx={shellSx}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose your loan workflow, program, and occupancy on the Loan type tab
          to see your document checklist.
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate('/loan/loan-type')}
        >
          Go to Loan type
        </Button>
      </Shell>
    );
  }

  return (
    <Shell sx={shellSx}>
      <Typography
        variant={variant === 'page' ? 'subtitle1' : 'subtitle2'}
        fontWeight={600}
        gutterBottom
      >
        Mortgage approval file
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        One checklist for your profile and mortgage application — uploads and
        completion status stay in sync everywhere you manage documents.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Required documents: {progress.done} / {progress.total}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress.pct}
          sx={{ mt: 0.5, height: 8, borderRadius: 1 }}
        />
      </Box>
      {showOpenApplicationButton && variant === 'page' && (
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/loan/${file._id}`)}
          sx={{ mb: 2 }}
        >
          Open mortgage application
        </Button>
      )}
      {variant === 'page' && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Documents and conditions
        </Typography>
      )}
      <ChecklistSection application={file} readOnly={checklistReadOnly} />
    </Shell>
  );
}
